/* eslint-disable no-nested-ternary */

import moment from 'moment';
import PropTypes from 'prop-types';
import React, {
  useId,
  useState,
} from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  Box,
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useColorScheme,
  useMediaQuery,
} from '@mui/material';
import {
  lighten,
  useTheme,
} from '@mui/material/styles';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Today from '@mui/icons-material/Today';
import {
  NotificationsActive,
  ViewList,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import Grid from '@mui/material/Grid';

import {
  setPlannerGridView,
  setPlannerShowReminders,
} from '../../../../../../redux/planner/actions';
import { primary } from '../../../../../../utils/brandColors';
import { getPlannerReminderSummary } from '../../../../../../utils/reminderHelpers';

const ToolbarComponent = ({
  gridView,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  navigate,
}) => {
  const theme = useTheme();
  const {
    mode,
    systemMode,
  } = useColorScheme();
  const darkMode = mode === 'dark' || (mode === 'system' && systemMode === 'dark') || (!mode && theme.palette.mode === 'dark');
  const mediumDown = useMediaQuery(theme.breakpoints.down('md'));
  const [reminderAnchorEl, setReminderAnchorEl] = useState(null);
  const reminderMenuId = useId();
  const reminderButtonId = useId();
  const remindersOpen = !!reminderAnchorEl;
  const dispatch = useDispatch();
  const isDayView = gridView === 'day';

  // Redux
  const showReminders = useSelector((state) => state.planner.showReminders !== false);
  const upcomingDueDates = useSelector((state) => state.board.upcomingDueDates);
  const boardHorses = useSelector((state) => state.board.barnHorses);
  const currentHorseTags = new Set(boardHorses.map((horse) => Number(horse.pingumTagId)));
  const reminders = upcomingDueDates.filter((reminder) => currentHorseTags.has(reminder.tag?.id));
  const summary = getPlannerReminderSummary(reminders);
  const numberOfDueDates = summary.reminderCount;
  const reminderLabel = numberOfDueDates === 1
    ? 'reminder'
    : 'reminders';
  const horseLabel = summary.horseCount === 1
    ? 'horse'
    : 'horses';
  const reminderTooltip = `${numberOfDueDates} ${reminderLabel} for ${summary.horseCount} ${horseLabel} overdue or due within 14 days. Reminder options.`;
  let reminderIconColor = 'inherit';
  if (numberOfDueDates > 0) {
    reminderIconColor = darkMode
      ? lighten(theme.palette.error.main, 0.65)
      : theme.palette.error.main;
  }
  const visibilityLabel = showReminders
    ? 'Hide reminders in planner'
    : 'Show reminders in planner';
  const VisibilityIcon = showReminders
    ? VisibilityOff
    : Visibility;
  const reminderButtonProps = {
    id: reminderButtonId,
    'aria-label': 'Reminders',
    'aria-haspopup': 'menu',
    'aria-expanded': remindersOpen,
    'aria-controls': remindersOpen
      ? reminderMenuId
      : undefined,
    onClick: (event) => setReminderAnchorEl(event.currentTarget),
    size: 'small',
  };
  let reminderButton = (
    <IconButton {...reminderButtonProps}>
      <NotificationsActive sx={{ color: reminderIconColor }} />
    </IconButton>
  );
  if (!mediumDown) {
    reminderButton = (
      <Button {...reminderButtonProps} variant="outlined" startIcon={<NotificationsActive sx={{ color: reminderIconColor }} />}>
        Reminders
      </Button>
    );
  }
  return (
    <Grid container direction="row" justifyContent="flex-start" alignItems="center">
      <Grid
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
        size={{
          xs: 5,
          md: 3,
        }}
      >
        <Tooltip title="Go to Today">
          <Button
            sx={{ mr: 2 }}
            variant="outlined"
            startIcon={<Today />}
            size="small"
            onClick={() => {
              const date = moment().startOf('day').toDate();
              if (isDayView) {
                setStartDate(moment(date).startOf('day').toDate());
                setEndDate(moment(date).endOf('day').toDate());
              } else {
                setStartDate(
                  moment(date).startOf('week').startOf('day').toDate(),
                );
                setEndDate(
                  moment(date).endOf('week').endOf('day').toDate(),
                );
              }
            }}
          >Today
          </Button>
        </Tooltip>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <Tooltip title="Previous">
            <IconButton
              onClick={() => {
                if (isDayView) {
                  const date = moment(startDate).add(-1, 'days').startOf('day').toDate();
                  setStartDate(moment(date).startOf('day').toDate());
                  setEndDate(moment(date).endOf('day').toDate());
                } else {
                  setStartDate(
                    moment(startDate).add(-1, 'weeks').startOf('week')
                      .startOf('day')
                      .toDate(),
                  );
                  setEndDate(
                    moment(startDate).add(-1, 'weeks').endOf('week')
                      .endOf('day')
                      .toDate(),
                  );
                }
              }}
              size="small"
              sx={{ backgroundColor: primary.main }}
            ><ChevronLeft sx={{ color: '#ffffff' }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Next">
            <IconButton
              size="small"
              variant="contained"
              sx={{ backgroundColor: primary.main }}
              onClick={() => {
                if (isDayView) {
                  const date = moment(startDate).add(1, 'days').startOf('day').toDate();
                  setStartDate(moment(date).startOf('day').toDate());
                  setEndDate(moment(date).endOf('day').toDate());
                } else {
                  setStartDate(
                    moment(startDate).add(1, 'weeks').startOf('week')
                      .startOf('day')
                      .toDate(),
                  );
                  setEndDate(
                    moment(startDate).add(1, 'weeks').endOf('week')
                      .endOf('day')
                      .toDate(),
                  );
                }
              }}
            ><ChevronRight sx={{ color: '#ffffff' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Grid>
      {
        !mediumDown
          ? (
            <Grid align="center" size={5}>
              <Typography variant="subtitle1" color="primary" align="center">{
                isDayView
                  ? moment(startDate).format('ll')
                  : `${moment(startDate).format('ll')} - ${moment(endDate).format('ll')}`
              }
              </Typography>
            </Grid>
          )
          : null
      }
      <Grid
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
        }}
        size={{
          xs: 7,
          md: 4,
        }}
      >
        <Grid
          container
          spacing={1}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <Grid>
            <Tooltip title={reminderTooltip}>{reminderButton}</Tooltip>
            <Menu
              id={reminderMenuId}
              anchorEl={reminderAnchorEl}
              open={remindersOpen}
              onClose={() => setReminderAnchorEl(null)}
              MenuListProps={{ 'aria-labelledby': reminderButtonId }}
            >
              <MenuItem
                disabled={!navigate}
                onClick={() => {
                  setReminderAnchorEl(null);
                  navigate('/tasks/reminders');
                }}
              >
                <ListItemIcon><ViewList fontSize="small" /></ListItemIcon>
                <ListItemText>View all reminders</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  dispatch(setPlannerShowReminders(!showReminders));
                  setReminderAnchorEl(null);
                }}
              >
                <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary={visibilityLabel} secondary="Remembered on this device" />
              </MenuItem>
            </Menu>
          </Grid>
          <Grid>
            <ToggleButtonGroup
              color="primary"
              value={isDayView
                ? 'day'
                : 'week'}
              exclusive
              onChange={(e, newView) => {
                if (newView === 'week' && isDayView) {
                  dispatch(setPlannerGridView('week'));
                  setStartDate(moment(startDate).startOf('week').startOf('day').toDate());
                  setEndDate(moment(startDate).endOf('week').endOf('day').toDate());
                } else if (newView === 'day' && !isDayView) {
                  dispatch(setPlannerGridView('day'));
                  const todayMoment = moment().startOf('day');
                  const startOfRange = moment(startDate).startOf('day');
                  const endOfRange = moment(endDate).endOf('day');
                  const selectedDayTimestamp = todayMoment.isBetween(startOfRange, endOfRange, 'day', '[]')
                    ? todayMoment.toDate().getTime()
                    : startOfRange.toDate().getTime();
                  setStartDate(moment(selectedDayTimestamp).startOf('day').toDate());
                  setEndDate(moment(selectedDayTimestamp).endOf('day').toDate());
                }
              }}
              aria-label="Calendar View"
            >
              <ToggleButton size="small" value="day">Day</ToggleButton>
              <ToggleButton size="small" value="week">Week</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

ToolbarComponent.propTypes = {
  gridView: PropTypes.string.isRequired,
  setStartDate: PropTypes.func.isRequired,
  setEndDate: PropTypes.func.isRequired,
  startDate: PropTypes.any.isRequired,
  endDate: PropTypes.any.isRequired,
  navigate: PropTypes.func,
};
ToolbarComponent.defaultProps = { navigate: null };

export default (ToolbarComponent);
